import { GroupElementModel } from '@blocksuite/affine-model';
import type { EditorHost } from '@blocksuite/block-std';
import type { EdgelessRootService } from '@blocksuite/blocks';
import { Bound, getCommonBound } from '@blocksuite/global/utils';
import type { BlockSnapshot } from '@blocksuite/store';

import { markdownToSnapshot } from '../_common/markdown-utils';
import { getSurfaceElementFromEditor } from '../_common/selection-utils';
import {
  basicTheme,
  type PPTDoc,
  type PPTSection,
  type TemplateImage,
} from './template';

export const PPTBuilder = (host: EditorHost) => {
  const service = host.spec.getService<EdgelessRootService>('affine:page');
  const docs: PPTDoc[] = [];
  const contents: unknown[] = [];
  const allImages: TemplateImage[][] = [];
  const slideBounds: Array<{ x: number; y: number; w: number; h: number }> = [];
  const SLIDE_VERTICAL_GAP = 240;
  let nextSlideTop: number | null = null;
  let slideBaseX: number | null = null;

  const addDoc = async (block: BlockSnapshot) => {
    const sections = block.children.map(v => {
      const title = getText(v);
      const child0 = v.children[0];
      const child1 = v.children[1];
      const keywords = child0 ? getText(child0) : '';
      const content = child1 ? getText(child1) : '';
      return {
        title,
        keywords,
        content,
      } satisfies PPTSection;
    });
    const doc: PPTDoc = {
      isCover: docs.length === 0,
      title: getText(block),
      sections,
    };
    docs.push(doc);

    if (doc.isCover) return;
    const job = service.createTemplateJob('template');
    const { images, content } = await basicTheme(doc);
    contents.push(content);
    allImages.push(images);
    if (images.length) {
      await Promise.all(
        images.map(async ({ id, url, width, height }) => {
          const res = await fetch(url);
          let blob = await res.blob();

          // If it's an SVG and we have dimensions, scale it
          if (blob.type === 'image/svg+xml' && width && height) {
            const text = await blob.text();

            // Parse the SVG and add/modify width, height, and viewBox
            const parser = new DOMParser();
            const svgDoc = parser.parseFromString(text, 'image/svg+xml');
            const svgElement = svgDoc.documentElement;

            // Get original viewBox or dimensions
            let viewBox = svgElement.getAttribute('viewBox');
            if (!viewBox) {
              const origWidth = svgElement.getAttribute('width') || '100';
              const origHeight = svgElement.getAttribute('height') || '100';
              viewBox = `0 0 ${parseFloat(origWidth)} ${parseFloat(origHeight)}`;
            }

            // Set new dimensions while preserving aspect ratio via viewBox
            svgElement.setAttribute('viewBox', viewBox);
            svgElement.setAttribute('width', width.toString());
            svgElement.setAttribute('height', height.toString());
            svgElement.setAttribute('preserveAspectRatio', 'xMidYMid meet');

            // Convert back to blob
            const scaledSvg = new XMLSerializer().serializeToString(svgDoc);
            blob = new Blob([scaledSvg], { type: 'image/svg+xml' });
          }

          job.job.assets.set(id, blob);
        })
      );
    }
    const templateBound = await job.insertTemplate(content);
    getSurfaceElementFromEditor(host).refresh();

    // Group all elements in this slide
    if (templateBound) {
      try {
        const picked = service.pickElementsByBound(templateBound) as BlockSuite.EdgelessModel[];

        if (picked.length > 0) {
          const groupId = service.createGroup(picked);
          const group = service.getElementById(groupId);

          if (group instanceof GroupElementModel) {
            group.showTitle = false;
          }

          const slideElements = group ? [...picked, group] : picked;
          const slideBound = getCommonBound(
            slideElements
              .map(element => {
                const xywh = (element as { xywh?: unknown }).xywh;
                if (typeof xywh === 'string') {
                  try {
                    return Bound.deserialize(xywh);
                  } catch (_) {
                    return null;
                  }
                }
                if ('elementBound' in element && element.elementBound) {
                  return element.elementBound as Bound;
                }
                return null;
              })
              .filter((bound): bound is Bound => bound !== null)
          );

          if (slideBound) {
            if (slideBounds.length === 0) {
              slideBaseX = slideBound.x;
            }

            const targetX = slideBaseX ?? slideBound.x;
            const targetY =
              slideBounds.length === 0
                ? slideBound.y
                : nextSlideTop ?? slideBound.y;
            const offsetX = targetX - slideBound.x;
            const offsetY = targetY - slideBound.y;
            let finalBound = slideBound;

            if (offsetX !== 0 || offsetY !== 0) {
              service.doc.transact(() => {
                slideElements.forEach(element => {
                  const xywh = (element as { xywh?: unknown }).xywh;
                  if (typeof xywh !== 'string') return;

                  const bound = Bound.deserialize(xywh);
                  bound.x += offsetX;
                  bound.y += offsetY;

                  service.updateElement(element.id, {
                    xywh: bound.serialize(),
                  });
                });
              });

              finalBound = new Bound(
                slideBound.x + offsetX,
                slideBound.y + offsetY,
                slideBound.w,
                slideBound.h
              );
            }

            slideBounds.push({
              x: finalBound.x,
              y: finalBound.y,
              w: finalBound.w,
              h: finalBound.h,
            });
            nextSlideTop = finalBound.y + finalBound.h + SLIDE_VERTICAL_GAP;
          }
        }
      } catch (err) {
        console.error('[PPTBuilder] Failed to group slide elements:', err);
      }
    }
  };

  return {
    process: async (text: string) => {
      try {
        slideBounds.length = 0;
        nextSlideTop = null;
        slideBaseX = null;

        const snapshot = await markdownToSnapshot(text, host);

        const block = snapshot.snapshot.content[0];
        if (!block) {
          return { contents, images: allImages, bounds: slideBounds };
        }
        for (const child of block.children) {
          await addDoc(child);
          const { centerX, centerY, zoom } = service.getFitToScreenData();
          service.viewport.setViewport(zoom, [centerX, centerY]);
        }
      } catch (e) {
        console.error(e);
      }

      return { contents, images: allImages, bounds: slideBounds };
    },
    done: async (text: string) => {
      const snapshot = await markdownToSnapshot(text, host);
      const block = snapshot.snapshot.content[0];
      if (!block) return;
      const lastChild = block.children[block.children.length - 1];
      if (!lastChild) return;
      await addDoc(lastChild);
    },
  };
};

const getText = (block: BlockSnapshot) => {
  const props = block.props as { text?: { delta?: Array<{ insert?: string }> } };
  return props.text?.delta?.[0]?.insert ?? '';
};
